/**
 * MemberList - 成员列表组件
 * 显示 Deal 的所有成员，支持续期和移除操作
 */
import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  User, Crown, Users, Shield, Clock, 
  RefreshCw, Trash2, MoreVertical, AlertTriangle,
  Loader2
} from 'lucide-react'
import { DealMember, renewMember, removeMember, getRoleDisplayName } from '../../services/member'
import { DealRole } from '../../services/invitation'
import { parseApiError } from '../../services/api'

interface MemberListProps {
  members: DealMember[]
  currentUserRole: DealRole
  onMemberUpdated: () => void
}

export default function MemberList({ 
  members, 
  currentUserRole,
  onMemberUpdated 
}: MemberListProps) {
  const [loadingMemberId, setLoadingMemberId] = useState<string | null>(null)
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null)
  const [confirmRemoveId, setConfirmRemoveId] = useState<string | null>(null)
  
  const isOwner = currentUserRole === 'owner'
  
  const getRoleIcon = (role: DealRole) => {
    switch (role) {
      case 'owner':
        return <Crown className="w-4 h-4 text-amber-500" />
      case 'presales':
        return <Users className="w-4 h-4 text-blue-500" />
      case 'manager':
        return <Shield className="w-4 h-4 text-purple-500" />
    }
  }
  
  const getRoleBgColor = (role: DealRole) => {
    switch (role) {
      case 'owner':
        return 'bg-amber-50 border-amber-200'
      case 'presales':
        return 'bg-blue-50 border-blue-200'
      case 'manager':
        return 'bg-purple-50 border-purple-200'
    }
  }
  
  const handleRenew = async (memberId: string) => {
    setLoadingMemberId(memberId)
    try {
      await renewMember(memberId, 7)
      onMemberUpdated()
    } catch (err) {
      const apiError = parseApiError(err)
      console.error('续期失败:', apiError.message)
    } finally {
      setLoadingMemberId(null)
      setMenuOpenId(null)
    }
  }
  
  const handleRemove = async (memberId: string) => {
    setLoadingMemberId(memberId)
    try {
      await removeMember(memberId)
      onMemberUpdated()
    } catch (err) {
      const apiError = parseApiError(err)
      console.error('移除失败:', apiError.message)
    } finally {
      setLoadingMemberId(null)
      setConfirmRemoveId(null)
      setMenuOpenId(null)
    }
  }
  
  // 按角色排序：Owner > Pre-sales > Manager
  const sortedMembers = [...members].sort((a, b) => {
    const roleOrder = { owner: 0, presales: 1, manager: 2 }
    return roleOrder[a.role] - roleOrder[b.role]
  })
  
  return (
    <div className="space-y-2">
      {sortedMembers.map((member) => {
        const isExpiringSoon = member.daysUntilExpiry !== undefined && 
                               member.daysUntilExpiry <= 3 && 
                               member.role !== 'owner'
        
        return (
          <motion.div
            key={member.id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`relative flex items-center justify-between p-3 rounded-lg border ${getRoleBgColor(member.role)}`}
          >
            {/* 成员信息 */}
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-sm">
                <User className="w-5 h-5 text-gray-400" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-medium text-gray-900">{member.username}</span>
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                    member.role === 'owner' ? 'bg-amber-100 text-amber-700' :
                    member.role === 'presales' ? 'bg-blue-100 text-blue-700' :
                    'bg-purple-100 text-purple-700'
                  }`}>
                    {getRoleIcon(member.role)}
                    {getRoleDisplayName(member.role)}
                  </span>
                </div>
                <p className="text-sm text-gray-500">{member.email}</p>
              </div>
            </div>
            
            {/* 右侧信息和操作 */}
            <div className="flex items-center gap-3">
              {/* 过期信息 */}
              {member.role !== 'owner' && member.daysUntilExpiry !== undefined && (
                <div className={`flex items-center gap-1 text-xs ${
                  member.isExpired ? 'text-red-500' :
                  isExpiringSoon ? 'text-amber-500' :
                  'text-gray-400'
                }`}>
                  {member.isExpired ? (
                    <AlertTriangle className="w-3.5 h-3.5" />
                  ) : (
                    <Clock className="w-3.5 h-3.5" />
                  )}
                  <span>
                    {member.isExpired 
                      ? '已过期' 
                      : `${member.daysUntilExpiry}天后过期`}
                  </span>
                </div>
              )}
              
              {/* Owner 操作菜单 */}
              {isOwner && member.role !== 'owner' && (
                <div className="relative">
                  <button
                    onClick={() => setMenuOpenId(menuOpenId === member.id ? null : member.id)}
                    className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-white/50 rounded-lg transition-colors"
                  >
                    <MoreVertical className="w-4 h-4" />
                  </button>
                  
                  <AnimatePresence>
                    {menuOpenId === member.id && (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        className="absolute right-0 top-full mt-1 w-36 bg-white rounded-lg shadow-lg border border-gray-100 py-1 z-10"
                      >
                        <button
                          onClick={() => handleRenew(member.id)}
                          disabled={loadingMemberId === member.id}
                          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                        >
                          {loadingMemberId === member.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <RefreshCw className="w-4 h-4" />
                          )}
                          续期 7 天
                        </button>
                        <button
                          onClick={() => setConfirmRemoveId(member.id)}
                          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                          移除成员
                        </button>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )}
            </div>
            
            {/* 确认移除弹窗 */}
            <AnimatePresence>
              {confirmRemoveId === member.id && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="absolute inset-0 bg-white/95 rounded-lg flex items-center justify-center gap-3 z-20"
                >
                  <span className="text-sm text-gray-600">确定移除此成员？</span>
                  <button
                    onClick={() => handleRemove(member.id)}
                    disabled={loadingMemberId === member.id}
                    className="px-3 py-1.5 bg-red-500 text-white text-sm rounded-lg hover:bg-red-600 transition-colors"
                  >
                    {loadingMemberId === member.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      '确定'
                    )}
                  </button>
                  <button
                    onClick={() => setConfirmRemoveId(null)}
                    className="px-3 py-1.5 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    取消
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        )
      })}
      
      {members.length === 0 && (
        <div className="text-center py-8 text-gray-400">
          <Users className="w-8 h-8 mx-auto mb-2" />
          <p className="text-sm">暂无成员</p>
        </div>
      )}
    </div>
  )
}

